import { createContext, useContext, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginScopes, ADMIN_GROUP_ID, BREAK_GLASS_ADMIN_EMAIL } from "./msalConfig";
import { groupsFromClaims, decideAdmin } from "./adminResolver";

// ── Who is an admin? ──────────────────────────────────────────────────────────
// Membership of the Entra security group SWA-ESTIMATING-ADMIN (msalConfig.js
// ADMIN_GROUP_ID), resolved ONCE per signed-in account and shared through
// context. Replaces the hardcoded single-email check that used to be pasted
// into every Setup page and the nav (2026-09-11).
//
// Resolution (see adminResolver.js): the ID token's `groups` claim when the
// app registration emits one; otherwise one Graph call —
// POST /me/checkMemberGroups — using the login scopes the app already holds
// (the same Graph access TurnoverDetail's directory lookup uses; no new
// consent). Only if neither can answer does the break-glass email apply.
//
// isAdmin is `null` while resolving, then true/false. Gate a page with
//   const isAdmin = useIsAdmin();
//   if (isAdmin !== true) return <AdminGate isAdmin={isAdmin} />;
// so it shows "Checking access…" rather than flashing "Not authorized".
//
// This is UI gating. The SQL proxy enforces the same group server-side for
// admin-only procs (cs-est-sql-api/shared/adminProcs.js) once the API token
// carries the groups claim — see CLAUDE.md "Admin authorization".

const AdminCtx = createContext({ isAdmin: null, source: null, error: null });

export function AdminProvider({ children }) {
  const { instance, accounts } = useMsal();
  const account = accounts[0] || null;
  const accountKey = account?.homeAccountId || account?.username || null;
  const [state, setState] = useState({ isAdmin: null, source: null, error: null });

  useEffect(() => {
    let cancelled = false;
    if (!account) { setState({ isAdmin: false, source: "signed-out", error: null }); return undefined; }
    setState({ isAdmin: null, source: null, error: null });
    const base = { email: account.username, adminGroupId: ADMIN_GROUP_ID, breakGlassEmail: BREAK_GLASS_ADMIN_EMAIL };

    const claimGroups = groupsFromClaims(account.idTokenClaims);
    if (claimGroups) {
      setState(decideAdmin({ ...base, claimGroups }));
      return undefined;
    }

    (async () => {
      let graphGroups = null, graphError = null;
      try {
        const tok = await instance.acquireTokenSilent({ scopes: loginScopes, account });
        const res = await fetch("https://graph.microsoft.com/v1.0/me/checkMemberGroups", {
          method: "POST",
          headers: { Authorization: `Bearer ${tok.accessToken}`, "Content-Type": "application/json" },
          body: JSON.stringify({ groupIds: [ADMIN_GROUP_ID] }),
        });
        if (!res.ok) throw new Error(`Graph checkMemberGroups ${res.status}`);
        const json = await res.json();
        graphGroups = Array.isArray(json?.value) ? json.value : [];
      } catch (e) {
        graphError = e?.message || String(e);
      }
      if (cancelled) return;
      const decision = decideAdmin({ ...base, claimGroups: null, graphGroups, graphError });
      if (decision.source === "break-glass" || decision.source === "undetermined") {
        console.warn(`[admin] group membership could not be resolved (${graphError}); source=${decision.source}`);
      }
      setState(decision);
    })();
    return () => { cancelled = true; };
  }, [accountKey, instance]); // eslint-disable-line react-hooks/exhaustive-deps

  return <AdminCtx.Provider value={state}>{children}</AdminCtx.Provider>;
}

// { isAdmin: true|false|null, source, error }
// true | false | null (still checking)
export function useIsAdmin() { return useContext(AdminCtx).isAdmin; }

// Standard gate body for an admin-only page.
export function AdminGate({ isAdmin }) {
  const style = { padding: 32, color: "var(--text3)", fontSize: 13 };
  if (isAdmin === null) return <div style={style}>Checking access…</div>;
  return (
    <div style={style}>
      Not authorized. This page is limited to members of the <strong>SWA-ESTIMATING-ADMIN</strong> group
      — ask an administrator to add you in Entra ID.
    </div>
  );
}
